/**
 * ContactSection — "(b) Contact" on the one-page Confirm & book. The email is
 * PREFILLED from the rate-gate unlock (state.cache.userEmailBlurCards) with
 * the "from your unlock ✎" affordance; it stays fully editable. Inline
 * validation on blur, phone validated with libphonenumber (same rule as the
 * legacy CustomerForm).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Radio } from 'antd';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import PhoneInputDropdown from '../PhoneInputDropdown';
import {
  SectionCard,
  SectionHeading,
  SectionHint,
  FieldGrid,
  FullRow,
  fieldLabelStyle,
  PrefillTag,
} from './confirmTheme';

const label = (text) => <span style={fieldLabelStyle}>{text}</span>;

const validatePhoneNumber = (rule, value, callback) => {
  if (!value) {
    callback('Please add a phone number so we can reach you about this shipment.');
    return;
  }
  const phoneNumber = parsePhoneNumberFromString(value);
  if (!phoneNumber || !phoneNumber.isValid()) {
    callback('That phone number doesn’t look complete — please check it.');
    return;
  }
  callback();
};

const ContactSection = ({ form, prefilledEmail }) => {
  const { getFieldDecorator, getFieldValue } = form;
  const currentEmail = getFieldValue('email');
  const showPrefillTag =
    !!prefilledEmail &&
    (currentEmail === undefined || currentEmail === prefilledEmail);

  return (
    <SectionCard aria-label="Contact details">
      <SectionHeading>Contact</SectionHeading>
      <SectionHint>
        Who should we talk to about this booking? Your confirmation goes here.
      </SectionHint>

      <Form.Item label={label('Your role in this shipment')} colon={false}>
        {getFieldDecorator('role', {
          initialValue: 'Importer',
          rules: [{ required: true, message: 'Please pick your role.' }],
        })(
          <Radio.Group name="role">
            <Radio value="Importer">Importer / Receiver</Radio>
            <Radio value="Exporter">Exporter / Shipper</Radio>
          </Radio.Group>
        )}
      </Form.Item>

      <FieldGrid>
        <Form.Item label={label('First name')} colon={false}>
          {getFieldDecorator('first_name', {
            validateTrigger: 'onBlur',
            rules: [{ required: true, message: 'Please add your first name.' }],
          })(<Input size="large" autoComplete="given-name" placeholder="First name" />)}
        </Form.Item>
        <Form.Item label={label('Last name')} colon={false}>
          {getFieldDecorator('last_name', {
            validateTrigger: 'onBlur',
            rules: [{ required: true, message: 'Please add your last name.' }],
          })(<Input size="large" autoComplete="family-name" placeholder="Last name" />)}
        </Form.Item>
        <FullRow>
          <Form.Item
            colon={false}
            label={
              <span style={fieldLabelStyle}>
                Email
                {showPrefillTag && (
                  <PrefillTag>
                    from your unlock{' '}
                    <span role="img" aria-label="editable">
                      ✎
                    </span>
                  </PrefillTag>
                )}
              </span>
            }
          >
            {getFieldDecorator('email', {
              initialValue: prefilledEmail || undefined,
              validateTrigger: 'onBlur',
              rules: [
                { required: true, message: 'Please add your email.' },
                { type: 'email', message: 'That email doesn’t look right — please check it.' },
              ],
            })(<Input size="large" type="email" autoComplete="email" placeholder="you@company.com" />)}
          </Form.Item>
        </FullRow>
        <FullRow>
          <Form.Item label={label('Phone')} colon={false}>
            {getFieldDecorator('phone', {
              validateTrigger: 'onBlur',
              rules: [{ validator: validatePhoneNumber }],
            })(<PhoneInputDropdown />)}
          </Form.Item>
        </FullRow>
      </FieldGrid>
    </SectionCard>
  );
};

ContactSection.propTypes = {
  /** antd form instance (shared page form) */
  form: PropTypes.object.isRequired,
  /** state.cache.userEmailBlurCards — the unlock email */
  prefilledEmail: PropTypes.string,
};

export default ContactSection;
